import {
  Button,
  Label,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
  Switch,
} from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "../../../../../convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "../../../../../convex/schema";
import {
  type EgogrammeReponse,
  type EgogrammeReponses,
  type FormTemplateProps,
  isBackendError,
} from "../../lib/types";
import {
  EGOGRAMME_QUESTIONS_PER_PAGE,
  egogrammeQuestions,
} from "./egogramme-data";
// Build Zod schema from questions
const schema = z.object(
  Object.fromEntries(
    egogrammeQuestions.map((q) => [q.name, z.boolean().default(false)]),
  ),
);

type FormData = z.infer<typeof schema>;

export function CandidateEgogrammeTemplate({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const {
    currentStep,
    setCurrentStep,
    setStartTime,
    isSubmitting,
    setIsSubmitting,
    reset,
  } = useFormStore();

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const totalPages = Math.ceil(
    egogrammeQuestions.length / EGOGRAMME_QUESTIONS_PER_PAGE,
  );

  // Convert responses to boolean for form
  const reponses = evaluation.donnees.reponses as EgogrammeReponses;
  const convertedInitialResponses = Object.fromEntries(
    Object.entries(reponses).map(([key, value]) => [key, value.response]),
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: convertedInitialResponses as FormData,
  });

  useEffect(() => {
    reset();
    setStartTime(Date.now());
    return () => {
      reset();
    };
  }, [reset, setStartTime]);

  // Build donnees object for backend
  const buildDonnees = (data: FormData) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.EGOGRAMME,
    reponses: egogrammeQuestions.reduce<EgogrammeReponses>(
      (acc, q) => ({
        ...acc,
        [q.name]: {
          name: q.name,
          label: q.label,
          profil: q.profil,
          response: data[q.name as keyof FormData] as boolean,
        },
      }),
      {},
    ),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const startIndex = currentStep * EGOGRAMME_QUESTIONS_PER_PAGE;
  const endIndex = startIndex + EGOGRAMME_QUESTIONS_PER_PAGE;
  const currentQuestions = egogrammeQuestions.slice(startIndex, endIndex);

  const progressPercentage = ((currentStep + 1) / totalPages) * 100;

  const goToPage = async (page: number, data: FormData) => {
    if (page >= 0 && page < totalPages) {
      await saveResponses(data);
      setCurrentStep(page);
    }
  };

  const isFirstPage = currentStep === 0;
  const isLastPage = currentStep === totalPages - 1;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mx-auto container py-8">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">Egogramme</h1>
          <p className="text-muted-foreground">
            Test de personnalite - 140 questions
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              Page {currentStep + 1} sur {totalPages}
            </span>
            <span>
              Questions {startIndex + 1}-
              {Math.min(endIndex, egogrammeQuestions.length)} sur{" "}
              {egogrammeQuestions.length}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div className="space-y-4">
          {currentQuestions.map((q, index) => (
            <FormField
              key={q.name}
              control={form.control}
              name={q.name}
              render={({ field }) => (
                <FormItem className="flex items-center justify-between p-4 border rounded-lg">
                  <Label
                    htmlFor={q.name}
                    className="flex-1 cursor-pointer pr-4"
                  >
                    <div className="flex flex-col">
                      <div>
                        <span className="text-muted-foreground mr-2">
                          {startIndex + index + 1}.
                        </span>
                        {q.label}
                      </div>
                      <FormMessage />
                    </div>
                  </Label>
                  <FormControl>
                    <Switch
                      id={q.name}
                      checked={field.value}
                      disabled={!editable}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
        </div>

        <div className="flex justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => goToPage(currentStep - 1, form.getValues())}
            disabled={isFirstPage}
            className="flex-1"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Precedent
          </Button>

          {isLastPage ? (
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => goToPage(currentStep + 1, form.getValues())}
              className="flex-1"
            >
              Suivant
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
